import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { CONFIG } from '../injection-tokens';
import { abTestConfigInterface } from '../interfaces/abTestConfig.interface';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { isPlatformBrowser } from '@angular/common';
import { TagManagerService } from '../../../core/services/tag-manager.service';
import { BROWSER_WINDOW } from '../../../core/injection-tokens/browser-window';
import { MouseflowService } from '../../../core/services/mouseflow.service';

/**
 * injectable TestService for client and server, using localStorage to keep track of
 * test scopes
 */
@Injectable({
  providedIn: 'root'
})
export class AbTestService {
  private storagePrefix = 'abTest';
  private isBrowser: boolean;
  // set storage 'abTestIgnoreTimeFrame' to 1 to ignore start/end for all tests
  private ignoreTimeframe = this.localStorageService.read('ignoreTestTimeFrame') ? true : false;

  constructor(
    @Inject(CONFIG) protected configs: abTestConfigInterface[],
    @Inject(PLATFORM_ID) platformId: object,
    protected localStorageService: LocalStorageService,
    protected analyticsService: AnalyticsService,
    protected tagManagerService: TagManagerService,
    private mouseflowSerivce: MouseflowService,
    @Optional() @Inject(BROWSER_WINDOW) private window
  ) {
    // clean up all old tests on boot up
    this.isBrowser = isPlatformBrowser(platformId);
    this.cleanupStorage();
  }

  getActiveTests(): { [key: string]: string } | undefined {
    let activeTests;

    const tests = this.localStorageService.getAllPrefixed(this.storagePrefix);
    if (tests.length) {
      activeTests = Object.fromEntries(
        tests.map(testKey => [
          testKey.replace(this.storagePrefix, ''),
          this.localStorageService.read(testKey)
        ])
      );
    }

    return activeTests;
  }

  /**
   * Tells you which version your test should be displayed.
   * This is the go to function in your services and
   * components to show/do different things depending on
   * the experiment
   * @param scope 'name' of your test
   * @returns one of the 'versions' configured in the module
   */
  getVersion(scope: string): string {
    const test = this.getTest(scope);

    // Check test timeframe and return default if appropriate
    if (!this.isBrowser || !this.testTimeframeIsActive(test)) {
      if (!this.ignoreTimeframe) {
        return test.versions[0];
      }
    }

    // If user already has a version, continue with it for the duration of the experiment
    let continuity = this.localStorageService.read(this.storagePrefix + scope);
    if (this.isBrowser) {
      const urlParams = new URLSearchParams(window.location.search);
      continuity = urlParams.get(this.storagePrefix + scope) ?? continuity;
    }
    if (continuity && test.versions.includes(continuity)) {
      return continuity;
    }

    // User currently has no version. Set one and report to analytics
    const fortuna = Math.random();
    const steps = 1 / test.versions.length;
    for (let i = 1; i <= test.versions.length; i++) {
      if (fortuna <= steps * i) {
        const ver = test.versions[i - 1];
        this.storeVersionForContinuity(test.scope, ver);
        this.reportToAnalytics(test, ver);
        // return also stops loop for that extra bit of performance
        return test.versions[i - 1];
      }
    }
  }

  /**
   * returns test from the configuration identified by the 'scope' name
   * @param scope 'name' of the test you want to access
   * @returns abTestConfigInterface containing all the juicy test options
   * @throws Error when test can not be found. Check your 'scope' and the test configuration!
   */
  private getTest(scope?: string): abTestConfigInterface {
    const test = this.configs.find(config => config.scope == scope);
    if (!test) {
      console.error(`Test [${scope}] is not defined`);
      throw new Error(`Test [${scope}] is not defined`);
    }
    return test;
  }

  /**
   * check if test should be displayed to the user or not
   * @param test the test you wand to check
   * @returns boolean true if test is running, false if not
   */
  private testTimeframeIsActive(test: abTestConfigInterface): boolean {
    if (test.startDate && test.endDate) {
      const now = Date.now();
      const start = new Date(test.startDate).getTime();
      const end = new Date(test.endDate).getTime();
      return start < now && now < end;
    }
    return true;
  }

  /**
   * writes version shown to the user for continuity reasons to the localStorage
   * @param scope name of the test
   * @param version version shown to the user
   */
  private storeVersionForContinuity(scope: string, version: string) {
    this.localStorageService.write(this.storagePrefix + scope, version);
  }

  /**
   * reports to analytics which experiment has now begun and which version is shown to the user
   * @param test the test to report
   * @param version the version shown to the user to report
   */
  private reportToAnalytics(test: abTestConfigInterface, version: string) {
    const index = test.versions.indexOf(version);
    this.analyticsService.activateExperiment(test.analyticsId, index);
    this.tagManagerService.activateExperiment(test, index);
    this.mouseflowSerivce.activateExperiment(test.scope, version);
  }

  /**
   * removes all tests in localStorage that are no longer defined
   */
  private cleanupStorage() {
    if (this.isBrowser) {
      const storedTests = this.localStorageService.getAllPrefixed(this.storagePrefix);
      if (storedTests.length) {
        storedTests.forEach(storedTest => {
          if (
            !this.configs.find(config => {
              return this.storagePrefix + config.scope == storedTest;
            })
          ) {
            // test was not found, remove it
            this.localStorageService.remove(storedTest);
          }
        });
      }
    }
  }
}
